import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit } from '@/types'
import {
  ClipboardCheck,
  Eye,
  Calendar,
  Users,
  UserCheck,
  Video,
  Filter,
  Loader2,
  Radio,
  BarChart3
} from 'lucide-react'

interface CohortOption {
  id: string
  name: string
  level_id: string
}

interface WorkshopRow {
  id: string
  title: string
  scheduled_date: string
  duration_minutes: number
  cohort_id: string | null
  teacher_id: string | null
  unit: { id: string; order_num: number; title: string } | null
  teacher_name: string | null
  teacher_status: 'attended' | 'absent'
  student_attended: number
  student_total: number
}

interface LiveWorkshop {
  id: string
  title: string
  scheduled_date: string
  duration_minutes: number
}

export default function AttendanceList() {
  const [workshops, setWorkshops] = useState<WorkshopRow[]>([])
  const [cohorts, setCohorts] = useState<CohortOption[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [liveWorkshop, setLiveWorkshop] = useState<LiveWorkshop | null>(null)

  // Filters
  const [selectedCohortId, setSelectedCohortId] = useState('')
  const [selectedUnitId, setSelectedUnitId] = useState('')
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })

  const generateMonthOptions = () => {
    const options = []
    const now = new Date()
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) })
    }
    return options
  }

  const checkLiveWorkshop = useCallback(async () => {
    const now = new Date()
    const { data } = await (supabase
      .from('unit_workshops') as any)
      .select('id, title, scheduled_date, duration_minutes')

    const live = (data || []).find((w: any) => {
      const start = new Date(w.scheduled_date)
      const end = new Date(start.getTime() + (w.duration_minutes || 60) * 60000)
      const windowStart = new Date(start.getTime() - 10 * 60000)
      const windowEnd = new Date(end.getTime() + 10 * 60000)
      return now >= windowStart && now <= windowEnd
    })

    setLiveWorkshop(live || null)
  }, [])

  const loadFilters = useCallback(async () => {
    const [cohortsRes, unitsRes] = await Promise.all([
      (supabase.from('cohorts') as any)
        .select('id, name, level_id')
        .eq('is_active', true)
        .order('name'),
      supabase.from('units')
        .select('*')
        .order('order_num', { ascending: true })
    ])
    setCohorts(cohortsRes.data || [])
    setUnits((unitsRes.data as unknown as Unit[]) || [])
  }, [])

  const loadWorkshops = useCallback(async () => {
    setIsLoading(true)
    try {
      let query = (supabase.from('unit_workshops') as any)
        .select(`
          id, title, scheduled_date, duration_minutes, cohort_id, teacher_id,
          unit:units!unit_id(id, order_num, title)
        `)
        .order('scheduled_date', { ascending: false })

      if (selectedCohortId) {
        query = query.eq('cohort_id', selectedCohortId)
      }
      if (selectedUnitId) {
        query = query.eq('unit_id', selectedUnitId)
      }

      const { data: workshopsData, error } = await query
      if (error) {
        console.error('Error loading workshops:', error)
        setWorkshops([])
        return
      }

      // Filter by month client-side
      let filtered = workshopsData || []
      if (selectedMonth) {
        const [year, month] = selectedMonth.split('-').map(Number)
        filtered = filtered.filter((w: any) => {
          const d = new Date(w.scheduled_date)
          return d.getFullYear() === year && d.getMonth() + 1 === month
        })
      }

      // Load relations for each workshop
      const enriched: WorkshopRow[] = await Promise.all(
        filtered.map(async (w: any) => {
          // Teacher name via teachers → profiles join
          let teacherName: string | null = null
          if (w.teacher_id) {
            const { data: teacherData } = await (supabase
              .from('teachers') as any)
              .select('id, profile:profiles!profile_id(first_name, last_name)')
              .eq('id', w.teacher_id)
              .single()
            if (teacherData?.profile) {
              teacherName = `${teacherData.profile.first_name} ${teacherData.profile.last_name}`
            }
          }

          // Teacher attendance
          let teacherStatus: 'attended' | 'absent' = 'absent'
          if (w.teacher_id) {
            const { data: tAtt } = await (supabase
              .from('teacher_workshop_attendance') as any)
              .select('status')
              .eq('workshop_id', w.id)
              .eq('teacher_id', w.teacher_id)
            if (tAtt && tAtt.length > 0 && tAtt[0].status === 'attended') {
              teacherStatus = 'attended'
            }
          }

          // Student attendance counts
          const { data: sAtt } = await (supabase
            .from('workshop_attendance') as any)
            .select('id, status')
            .eq('workshop_id', w.id)
          const studentAttended = (sAtt || []).filter((a: any) => a.status === 'attended').length

          // Total alumnos: desde la cohorte del taller o las cohortes del profesor
          let studentTotal = 0
          if (w.cohort_id) {
            const { data: cohortData } = await (supabase
              .from('cohorts') as any)
              .select('student_ids')
              .eq('id', w.cohort_id)
              .single()
            studentTotal = (cohortData?.student_ids || []).length
          } else if (w.teacher_id) {
            const { data: tRecord } = await (supabase
              .from('teachers') as any)
              .select('profile_id')
              .eq('id', w.teacher_id)
              .single()
            if (tRecord?.profile_id) {
              const { data: teacherCohorts } = await (supabase
                .from('cohorts') as any)
                .select('student_ids')
                .eq('teacher_id', tRecord.profile_id)
                .eq('is_active', true)
              const allIds = new Set((teacherCohorts || []).flatMap((c: any) => c.student_ids || []))
              studentTotal = allIds.size
            }
          }
          // Si no hay cohorte ni profesor, usar registros de asistencia como total
          if (studentTotal === 0) studentTotal = (sAtt || []).length

          return {
            id: w.id,
            title: w.title || '',
            scheduled_date: w.scheduled_date,
            duration_minutes: w.duration_minutes,
            cohort_id: w.cohort_id,
            teacher_id: w.teacher_id,
            unit: w.unit ? {
              id: w.unit.id,
              order_num: w.unit.order_num ?? 0,
              title: w.unit.title || ''
            } : null,
            teacher_name: teacherName,
            teacher_status: teacherStatus,
            student_attended: studentAttended,
            student_total: studentTotal
          }
        })
      )

      setWorkshops(enriched)
    } catch (err) {
      console.error('Error:', err)
    } finally {
      setIsLoading(false)
    }
  }, [selectedCohortId, selectedUnitId, selectedMonth])

  useEffect(() => {
    loadFilters()
    checkLiveWorkshop()
    const interval = setInterval(checkLiveWorkshop, 60000)
    return () => clearInterval(interval)
  }, [loadFilters, checkLiveWorkshop])

  useEffect(() => {
    loadWorkshops()
  }, [loadWorkshops])

  // Filter units by selected cohort's level
  const selectedCohort = cohorts.find(c => c.id === selectedCohortId)
  const filteredUnits = selectedCohort
    ? units.filter(u => (u as any).level_id === selectedCohort.level_id)
    : units

  // Stats
  const totalWorkshops = workshops.length
  const totalAttendances = workshops.reduce((sum, w) => sum + w.student_attended, 0)
  const avgAttendance = workshops.length > 0
    ? Math.round(workshops.reduce((sum, w) => {
        if (w.student_total === 0) return sum
        return sum + (w.student_attended / w.student_total) * 100
      }, 0) / workshops.filter(w => w.student_total > 0).length || 0)
    : 0

  if (isLoading && workshops.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Asistencia</h1>
        <p className="text-gray-600">Control histórico de asistencia a talleres</p>
      </div>

      {/* Live Workshop Banner */}
      {liveWorkshop ? (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Radio className="w-6 h-6 text-green-600" />
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full animate-ping" />
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full" />
            </div>
            <div>
              <p className="font-semibold text-green-800">Taller en curso</p>
              <p className="text-sm text-green-700">{liveWorkshop.title}</p>
            </div>
          </div>
          <Link
            to={`/admin/attendance/${liveWorkshop.id}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
          >
            <Eye className="w-4 h-4" />
            Ver Ahora
          </Link>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3">
          <Video className="w-6 h-6 text-gray-400" />
          <p className="text-gray-500">No hay taller activo en este momento</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#0055A4]/10">
              <ClipboardCheck className="w-5 h-5 text-[#0055A4]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalWorkshops}</p>
              <p className="text-sm text-gray-500">Total Talleres</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{totalAttendances}</p>
              <p className="text-sm text-gray-500">Total Asistencias</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <BarChart3 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{avgAttendance}%</p>
              <p className="text-sm text-gray-500">Promedio Asistencia</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex items-center gap-2 flex-1">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={selectedCohortId}
            onChange={(e) => {
              setSelectedCohortId(e.target.value)
              setSelectedUnitId('')
            }}
            className="flex-1 px-3 py-2 border rounded-lg bg-white text-sm"
          >
            <option value="">Todas las cohortes</option>
            {cohorts.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <select
          value={selectedUnitId}
          onChange={(e) => setSelectedUnitId(e.target.value)}
          className="px-3 py-2 border rounded-lg bg-white text-sm"
        >
          <option value="">Todas las unidades</option>
          {filteredUnits.map(u => (
            <option key={u.id} value={u.id}>
              Unidad {u.order_num}: {u.title}
            </option>
          ))}
        </select>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="px-3 py-2 border rounded-lg bg-white text-sm"
        >
          <option value="">Todos los meses</option>
          {generateMonthOptions().map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Workshops Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taller</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha y Hora</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Profesor</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Alumnos</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {workshops.length > 0 ? (
                workshops.map((w) => (
                  <tr key={w.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{w.title}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {w.unit ? `U${w.unit.order_num}: ${w.unit.title}` : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(w.scheduled_date).toLocaleDateString('es-CL', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                        {' '}
                        {new Date(w.scheduled_date).toLocaleTimeString('es-CL', {
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm text-gray-700">{w.teacher_name || 'Sin profesor'}</span>
                        {w.teacher_name && (
                          w.teacher_status === 'attended' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Asistio
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              No asistio
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className={`font-medium ${w.student_attended > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                          {w.student_attended}/{w.student_total}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Link
                        to={`/admin/attendance/${w.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[#0055A4] bg-[#0055A4]/5 rounded-lg hover:bg-[#0055A4]/10 transition-colors text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        Ver
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No se encontraron talleres para los filtros seleccionados</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
